/**
 * SQL 진단 리포트 — **DB 연결 없이** 쿼리문·실행계획 글만으로 진단 → 개선안(추정) → 최종안 → 리포트(docs/features/22-sql-review.md).
 *
 * SQL 튜닝 창과 같은 모양(왼쪽 작업 목록 · 단계 리본 · 리포트 보기)이지만 **측정이 없다** — 여기서 나오는 개선안은 전부 추정이고
 * 화면이 그 사실을 늘 말한다(머리의 띠 · 대안의 「추정」 · 리포트 머리말). SQL 을 실행하는 단추를 만들지 말 것(그것은 SQL 튜닝이다).
 * AI 단계는 사람도 한다(모든 칸을 손으로) · 지시를 붙여 다시 쓰기 · 중지 · 이력(서버가 쓴다) — AI 개발 가이드라인 2~5.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiFileText, FiPlus, FiTrash2, FiCpu, FiDownload, FiSquare, FiRefreshCw, FiArrowUp, FiArrowDown, FiBookOpen, FiPaperclip, FiSearch, FiAlertTriangle } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/sqlReview';
import { uploadTuningSources } from '../../services/assistantClient';
import * as guardrailApi from '../../services/guardrail';
import { guardExport } from '../../utils/guardrailGate';
import { alignPipeTables } from '../../utils/planAlign';
import PlanMarked from '../sqlTuning/PlanMarked';
import ReportView from '../sqlTuning/ReportView';
import { GuidePicker, CodeChips, matchKey } from '../sqlTuning/GuideCodes';
import './SqlReviewModal.css';
import memoWindow from '../common/memoWindow';
import WorkflowHead, { WorkflowRibbon, stepDirty } from '../common/WorkflowHead';

const STEPS = ['input', 'diagnose', 'alternatives', 'final', 'report'];
const TABS = [...STEPS, 'history'];
const ACCEPT = '.txt,.sql,.log,.md,.csv,.json,.png,.jpg,.jpeg,.gif,.webp,.pdf,.pptx';
const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));
const planIdsOf = (it) => (Array.isArray(it && it.planIds) ? it.planIds : []);
const toIds = (text) => String(text || '').split(/[\s,]+/).filter(Boolean).map(Number).filter(n => Number.isInteger(n) && n >= 0);

/** 입력 칸(쿼리·실행계획·참고 정보) + 자료 올리기·AI 로 읽기 — 새 작업과 1단계가 함께 쓴다 */
function InputFields({ value, onChange, aiEnabled, provider, disabled }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const stopRef = useRef(null);
  const [reading, setReading] = useState(false);
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const read = async (files) => {
    if (!files || !files.length) return;
    const ctl = new AbortController();
    stopRef.current = ctl;
    setReading(true);
    try {
      const up = await uploadTuningSources(Array.from(files));
      const ids = (up.sources || []).filter(s => s.id).map(s => s.id);
      if (!ids.length) { toast.warning(t('common:srv.readNothing')); return; }
      const r = await api.readSources({ sourceIds: ids, provider }, ctl.signal);
      // 칸을 채울 뿐이다 — 이미 적은 칸은 비어 있을 때만 채운다(쓴 것을 덮지 않게). 저장은 사람이
      onChange({
        ...value,
        sql: value.sql && value.sql.trim() ? value.sql : (r.fields.sql || ''),
        planText: value.planText && value.planText.trim() ? value.planText : alignPipeTables(r.fields.planText || ''),
        info: [value.info, r.fields.info].filter(x => x && String(x).trim()).join('\n'),
        sourceNote: [value.sourceNote, r.sourceNote].filter(Boolean).join(', '),
      });
      toast.success(t('common:srv.readDone'));
      if (r.imageUnscanned) toast.info(t('common:grd.imageUnscanned', { count: r.imageUnscanned }));
    } catch (err) {
      if (!ctl.signal.aborted) toast.error(formatApiError(err, t));
    } finally { setReading(false); stopRef.current = null; if (fileRef.current) fileRef.current.value = ''; }
  };
  return (
    <>
      {aiEnabled && (
        <div className="stn-row">
          <input ref={fileRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onChange={e => read(e.target.files)} />
          {reading
            ? <button type="button" className="app-btn app-btn-danger" onClick={() => stopRef.current && stopRef.current.abort()}><FiSquare size={12} /> {t('common:srv.stop')}</button>
            : <button type="button" className="app-btn stn-ai" onClick={() => fileRef.current && fileRef.current.click()} disabled={disabled}><FiPaperclip size={12} /> {t('common:srv.readFiles')}</button>}
          <span className="stn-note">{reading ? t('common:srv.reading') : t('common:srv.readHint')}</span>
        </div>
      )}
      <label className="stn-field"><span>{t('common:srv.sql')}</span>
        <textarea className="srv-mono" rows={8} value={value.sql || ''} onChange={set('sql')} placeholder={t('common:srv.sqlPh')} disabled={disabled} />
      </label>
      <label className="stn-field"><span>{t('common:srv.plan')}</span>
        {/* 칸에서 나가면 표의 칸을 다시 맞춘다 — 진단 탭·리포트와 같은 모양 */}
        <textarea className="srv-mono" rows={12} value={value.planText || ''} onChange={set('planText')} placeholder={t('common:srv.planPh')} disabled={disabled}
          onBlur={() => onChange({ ...value, planText: alignPipeTables(value.planText || '') })} />
      </label>
      <label className="stn-field"><span>{t('common:srv.info')}</span>
        <textarea rows={3} value={value.info || ''} onChange={set('info')} placeholder={t('common:srv.infoPh')} disabled={disabled} />
      </label>
      {value.sourceNote ? <div className="stn-note">{t('common:srv.sourceNote')}: {value.sourceNote}</div> : null}
    </>
  );
}
InputFields.propTypes = {
  value: PropTypes.shape({ sql: PropTypes.string, planText: PropTypes.string, info: PropTypes.string, sourceNote: PropTypes.string }).isRequired,
  onChange: PropTypes.func.isRequired, aiEnabled: PropTypes.bool, provider: PropTypes.string, disabled: PropTypes.bool,
};

/** AI 단추 — 그 단계에 이미 내용이 있으면 「다시 쓰기」(지금 내용 + 지시). 사람이 쓰거나 고친 항목은 남고 AI 항목만 갈아 끼운다(서버) */
function AiBar({ label, has, onRun, onStop, running, disabled }) {
  const { t } = useTranslation();
  const [instruction, setInstruction] = useState('');
  return (
    <div className="stn-row srv-aibar">
      {has && <input className="stn-grow" value={instruction} onChange={e => setInstruction(e.target.value)} placeholder={t('common:srv.instructionPh')} disabled={running} />}
      {running
        ? <button type="button" className="app-btn app-btn-danger" onClick={onStop}><FiSquare size={12} /> {t('common:srv.stop')}</button>
        : <button type="button" className="app-btn stn-ai" disabled={disabled} title={t('common:srv.aiUse')}
          onClick={() => { onRun(has ? instruction.trim() : ''); setInstruction(''); }}>
          <FiCpu size={12} /> {has ? t('common:srv.aiRewrite') : label}
        </button>}
      <span className="stn-note">{t('common:srv.aiUse')}</span>
    </div>
  );
}
AiBar.propTypes = { label: PropTypes.string, has: PropTypes.bool, onRun: PropTypes.func.isRequired, onStop: PropTypes.func, running: PropTypes.bool, disabled: PropTypes.bool };

const SqlReviewModal = ({ open, onClose, aiEnabled, aiProvider, seed, onSeedUsed, onRegisterCase, onOpenGuides, restore, onView, focus }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState('');
  const [job, setJob] = useState(null);
  const [d, setDraft] = useState(null);                 // 고치는 중인 문서
  const [problems, setProblems] = useState([]);
  const [step, setStep] = useState('input');
  const [creating, setCreating] = useState(null);       // 새 작업 양식 { title, sql, planText, info, sourceNote }
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState('');
  const [reportView, setReportView] = useState('full');
  const [markHot, setMarkHot] = useState(null);
  const [found, setFound] = useState(null);             // 닮은 사례 { cases, sharedCorpus }
  const stopRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const reload = useCallback(async () => {
    try { setJobs((await api.listJobs(q)).jobs || []); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [q]);
  useEffect(() => { if (open) reload(); }, [open, reload]);

  const adopt = useCallback((j, probs) => {
    setJob(j); setProblems(probs || []); setDraft(j ? JSON.parse(JSON.stringify(j.doc)) : null);
  }, []);

  // 새로고침 뒤 — 보던 작업·단계로 한 번만 되돌린다(App 이 workspaceLayout 에서 준 restore). 채팅·다른 화면이 가리킨 작업(focus·seed)이 있으면 그쪽이 앞선다.
  // 작업이 지워졌으면 조용히 목록만 보인다. 되돌리기가 끝난 뒤에야 위치를 알린다 — 먼저 알리면 「작업 없음」 이 저장된 위치를 덮는다.
  const [viewReady, setViewReady] = useState(false);
  useEffect(() => {
    if (!open || viewReady) return;
    const want = restore && restore.id && !(seed);
    if (!want) { setViewReady(true); return; }
    let alive = true;
    api.getJob(restore.id)
      .then((r) => { if (!alive) return; adopt(r.job, r.problems); if (restore.step && TABS.includes(restore.step)) setStep(restore.step); })
      .catch(() => { /* 지워진 작업 — 목록만 */ })
      .finally(() => { if (alive) setViewReady(true); });
    return () => { alive = false; };   // eslint-disable-line consistent-return
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 열릴 때 한 번
  }, [open]);
  const viewJobId = job ? job.id : null;
  useEffect(() => { if (open && viewReady && onView) onView({ id: viewJobId, step }); }, [open, viewReady, viewJobId, step, onView]);

  // 다른 화면에서 넘어온 자료(실행계획 탭의 「진단 리포트 만들기」) — 새 작업 양식에 채워 연다
  useEffect(() => {
    if (!open || !seed) return;
    setCreating({ title: seed.title || '', sql: seed.sql || '', planText: alignPipeTables(seed.planText || ''), info: seed.info || '', sourceNote: seed.sourceNote || '' });
    adopt(null);
    if (onSeedUsed) onSeedUsed();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 넘어온 자료가 바뀔 때만
  }, [open, seed && seed.nonce]);

  // 튜닝 가이드(이 작업에 실리는 것)와 분류 코드 목록 — 서버가 준다(SQL 튜닝 창과 같은 부품)
  const [guideInfo, setGuideInfo] = useState(null);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { if (open) api.getTopics().then(r => setCatalog(r.topics || [])).catch(() => setCatalog([])); }, [open]);
  const jobIdForGuide = job ? job.id : null;
  const jobStamp = job ? job.updatedAt : null;
  useEffect(() => {
    if (!open || !jobIdForGuide) { setGuideInfo(null); return; }
    api.getGuide(jobIdForGuide).then(r => setGuideInfo(r.guide)).catch(() => setGuideInfo(null));
  }, [open, jobIdForGuide, jobStamp]);

  const run = async (label, fn) => {
    setBusy(label);
    try { return await fn(); } catch (err) {
      if (!(stopRef.current && stopRef.current.signal.aborted)) toast.error(formatApiError(err, t));
      else toast.info(t('common:srv.stopped'));
      return null;
    } finally { setBusy(null); stopRef.current = null; }
  };
  const running = !!busy;

  const openJob = (id, wantStep) => run('open', async () => {
    const r = await api.getJob(id);
    adopt(r.job); setCreating(null); setReport(''); setFound(null);
    setStep(wantStep && TABS.includes(wantStep) ? wantStep : (r.job.status === 'input' ? 'input' : (r.job.status === 'final' ? 'report' : r.job.status)));
    reload();
  });
  // 채팅 에이전트가 가리킨 작업으로(sql_review_create·sql_review_open) — 화면이 열린 뒤 한 번
  const focusNonce = focus && focus.nonce;
  useEffect(() => {
    if (!open || !focus || !focus.id) return;
    openJob(focus.id, focus.step);
  }, [open, focusNonce]);   // eslint-disable-line react-hooks/exhaustive-deps -- nonce 가 바뀔 때만
  const create = () => run('create', async () => {
    const { title, ...input } = creating;
    const r = await api.createJob({ title, input });
    adopt(r.job); setCreating(null); setStep('diagnose'); setReport(''); setFound(null);
    reload();
  });
  const save = (patch, stepName) => run('save', async () => {
    const r = await api.patchJob(job.id, patch, { step: stepName });
    adopt(r.job, r.problems); reload();
    toast.success(t('common:stn.saved'), { autoClose: 1200 });
    return r;
  });
  const analyze = () => run('analyze', async () => {
    const r = await api.analyzeJob(job.id);
    adopt(r.job);
    if (r.note) toast.info(r.note);
  });
  const ai = (stepName, instruction) => run(`ai:${stepName}`, async () => {
    // 고치던 것이 있으면 먼저 저장한다 — AI 가 화면에 보이는 내용을 보고 쓰게
    const dirty = JSON.stringify(d) !== JSON.stringify(job.doc);
    if (dirty) await api.patchJob(job.id, { input: d.input, diagnosis: d.diagnosis, alternatives: d.alternatives, final: d.final, caseRef: d.caseRef, guideRef: d.guideRef }, { step: stepName });
    const ctl = new AbortController();
    stopRef.current = ctl;
    const r = await api.aiStep(job.id, stepName, { provider: aiProvider, instruction }, ctl.signal);
    adopt(r.job, r.problems); reload();
    if ((r.rejected || []).length) toast.warning(t('common:srv.aiRejected', { n: r.rejected.length, first: r.rejected[0].reason }));
    if (((r.guide && r.guide.dropped) || []).length) toast.warning(t('common:stn.guideDropped', { n: r.guide.dropped.length }));
    return r;
  });
  const stopAi = () => { if (stopRef.current) stopRef.current.abort(); };
  // 머리줄 「저장」 이 지금 단계에서 저장하는 칸 — 단계마다 맨 아래 있던 저장 단추를 대신한다(2026-10-26, common/WorkflowHead)
  const STEP_SAVE = {
    input: { fields: ['input'], patch: () => ({ input: d.input }) },
    diagnose: { fields: ['diagnosis'], patch: () => ({ diagnosis: d.diagnosis }) },
    alternatives: { fields: ['alternatives', 'caseRef'], patch: () => ({ alternatives: d.alternatives, caseRef: d.caseRef }) },
    final: { fields: ['final'], patch: () => ({ final: d.final }) },
  };
  const stepSave = job && d && !job.readOnly ? STEP_SAVE[step] : null;
  const stepIsDirty = !!stepSave && stepDirty(d, job.doc, stepSave.fields);
  const saveStep = () => { if (stepSave) save(stepSave.patch(), step); };
  const removeJob = async () => {
    const ok = await confirm({ title: t('common:stn.deleteTitle'), message: t('common:stn.deleteBody', { title: job.title }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    await run('delete', async () => { await api.deleteJob(job.id); adopt(null); reload(); });
  };

  const loadReport = useCallback(async (view = reportView) => {
    if (!job) return;
    try { setReport((await api.getReport(job.id, view)).markdown || ''); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [job, reportView]);
  useEffect(() => { if (open && job && step === 'report') { setReport(''); loadReport(reportView); } },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 단계·범위·작업이 바뀔 때
    [open, step, reportView, job && job.id, job && job.updatedAt]);
  const downloadReport = async () => {
    const g = await guardExport(report, { scan: guardrailApi.scanText, redact: guardrailApi.redactText });
    if (!g) return;
    if (g.mode === 'unchecked') toast.warning(t('common:grd.exportUnchecked'));
    const blob = new Blob([g.text], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sql-review-${(job.title || job.id).replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 60)}${reportView === 'brief' ? '-summary' : ''}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const registerCase = () => run('caseSeed', async () => { const r = await api.caseSeed(job.id); onRegisterCase(r.seed); });

  // ── 단계 그리기 ─────────────────────────────────────────────
  const renderInput = () => (
    <div className="stn-step">
      <InputFields value={d.input} onChange={input => setDraft({ ...d, input })} aiEnabled={aiEnabled} provider={aiProvider} disabled={running} />
      <div className="stn-box">
        <div className="stn-row stn-between">
          <b>{t('common:srv.symptoms')}</b>
          <button type="button" className="app-btn" onClick={analyze} disabled={running}><FiRefreshCw size={12} /> {t('common:srv.analyze')}</button>
        </div>
        {(job.doc.facts.symptoms || []).length
          ? <ul className="srv-symptoms">{job.doc.facts.symptoms.map(s => <li key={s.id || s.label}><b>{s.label}</b> — {s.why}</li>)}</ul>
          : <span className="stn-note">{job.doc.facts.note || (job.doc.facts.analyzedAt ? t('common:srv.noSymptoms') : t('common:srv.notAnalyzed'))}</span>}
      </div>
    </div>
  );

  const renderDiagnose = () => {
    const items = d.diagnosis.items || [];
    const notes = d.diagnosis.notes || [];
    const marks = new Map();
    items.forEach((it, i) => planIdsOf(it).forEach(id => marks.set(id, [...(marks.get(id) || []), it.id || `#${i + 1}`])));
    notes.forEach((n, i) => planIdsOf(n).forEach(id => marks.set(id, [...(marks.get(id) || []), `N${i + 1}`])));
    const setItem = (i, k, v) => setDraft({ ...d, diagnosis: { ...d.diagnosis, items: items.map((x, j) => (j === i ? { ...x, [k]: v, origin: 'user' } : x)) } });
    const setNotes = (list) => setDraft({ ...d, diagnosis: { ...d.diagnosis, notes: list } });
    return (
      <div className="stn-step">
        {/* 튜닝 가이드 — 연결이 없어 매핑은 못 쓴다: 자동이면 그 DBMS 의 범용 가이드. 고르면 바로 저장한다 */}
        <GuidePicker info={guideInfo} value={d.guideRef} disabled={running} onManage={onOpenGuides}
          onChange={(guideRef) => { setDraft({ ...d, guideRef }); save({ guideRef }, 'diagnose'); }} />
        {aiEnabled && <AiBar label={t('common:srv.aiDiagnose')} has={items.length > 0} onRun={ins => ai('diagnose', ins)} onStop={stopAi} running={busy === 'ai:diagnose'} disabled={running} />}
        {d.input.planText && d.input.planText.trim() ? (
          <div className="stn-box">
            <div className="stn-row stn-between"><b>{t('common:stn.planMarked')}</b><span className="stn-note">{marks.size ? t('common:stn.planMarkedHint') : t('common:stn.planMarkedNone')}</span></div>
            <PlanMarked planText={d.input.planText} marks={marks} active={markHot} onPick={setMarkHot} />
          </div>
        ) : <div className="stn-note">{t('common:srv.noPlan')}</div>}
        <textarea rows={2} value={d.diagnosis.summary || ''} placeholder={t('common:stn.summaryPh')} onChange={e => setDraft({ ...d, diagnosis: { ...d.diagnosis, summary: e.target.value } })} />
        {items.map((it, i) => (
          <div key={it.id || `new-${i}`} className={`stn-card stn-diag${markHot === it.id ? ' hot' : ''}`} onMouseEnter={() => setMarkHot(it.id)} onMouseLeave={() => setMarkHot(h => (h === it.id ? null : h))}>
            <div className="stn-row stn-between">
              <span className="stn-row">
                <span className="stn-pl-badge static">{it.id || `#${i + 1}`}</span>
                {it.origin === 'ai' && <span className="stn-badge">AI</span>}
                <label className="stn-planids" title={t('common:stn.planIdsHint')}>{t('common:stn.planIds')}
                  <input className="mono" value={planIdsOf(it).join(', ')} placeholder="4, 7" onChange={e => setItem(i, 'planIds', toIds(e.target.value))} />
                </label>
              </span>
              <button type="button" className="app-btn app-btn-danger" title={t('common:ui.delete')} onClick={() => setDraft({ ...d, diagnosis: { ...d.diagnosis, items: items.filter((_, j) => j !== i) } })}><FiTrash2 size={11} /></button>
            </div>
            <label className="stn-field"><span>{t('common:stn.point')}</span><input value={it.point || ''} onChange={e => setItem(i, 'point', e.target.value)} /></label>
            <div className="stn-grid2">
              <label className="stn-field"><span>{t('common:stn.symptom')}</span><textarea rows={3} value={it.symptom || ''} onChange={e => setItem(i, 'symptom', e.target.value)} /></label>
              <label className="stn-field"><span>{t('common:stn.evidence')}</span><textarea rows={3} value={it.evidence || ''} onChange={e => setItem(i, 'evidence', e.target.value)} /></label>
            </div>
            <label className="stn-field"><span>{t('common:stn.cause')}</span><textarea rows={2} value={it.cause || ''} onChange={e => setItem(i, 'cause', e.target.value)} /></label>
            <div className="stn-row stn-codeRow">
              <CodeChips label={t('common:stn.codes.symptom')} axis="symptom" codes={it.symptomCodes} catalog={catalog} onChange={v => setItem(i, 'symptomCodes', v)} />
              <CodeChips label={t('common:stn.codes.cause')} axis="cause" codes={it.causeCodes} catalog={catalog} onChange={v => setItem(i, 'causeCodes', v)} />
            </div>
          </div>
        ))}
        <div className="stn-box stn-notes">
          <div className="stn-row stn-between"><b>{t('common:stn.notesTitle')}</b><span className="stn-note">{t('common:stn.notesHintDiag')}</span></div>
          {notes.map((n, i) => (
            // eslint-disable-next-line react/no-array-index-key -- 참고 사항은 번호가 없다(순서가 곧 N 번호)
            <div key={i} className="stn-row stn-note-item">
              <span className="stn-pl-badge static note">{`N${i + 1}`}</span>
              {n.origin === 'ai' && <span className="stn-badge">AI</span>}
              <input className="mono stn-note-ids" value={planIdsOf(n).join(', ')} placeholder={t('common:stn.planIds')}
                onChange={e => setNotes(notes.map((x, j) => (j === i ? { ...x, planIds: toIds(e.target.value), origin: 'user' } : x)))} />
              <textarea className="stn-grow" rows={2} value={n.text || ''} onChange={e => setNotes(notes.map((x, j) => (j === i ? { ...x, text: e.target.value, origin: 'user' } : x)))} />
              <button type="button" className="app-btn app-btn-danger" title={t('common:ui.delete')} onClick={() => setNotes(notes.filter((_, j) => j !== i))}><FiTrash2 size={11} /></button>
            </div>
          ))}
          <div><button type="button" className="app-btn" onClick={() => setNotes([...notes, { text: '', planIds: [], origin: 'user' }])}><FiPlus size={12} /> {t('common:stn.addNote')}</button></div>
        </div>
        <div className="stn-actions">
          <button type="button" className="app-btn" onClick={() => setDraft({ ...d, diagnosis: { ...d.diagnosis, items: [...items, { planIds: [], point: '', symptom: '', cause: '', evidence: '', origin: 'user' }] } })}><FiPlus size={12} /> {t('common:stn.addItem')}</button>
        </div>
      </div>
    );
  };

  const renderAlternatives = () => {
    const alts = d.alternatives || [];
    const dIds = (d.diagnosis.items || []).map(x => x.id).filter(Boolean);
    const ref = d.caseRef || { enabled: true, ids: [] };
    const setAlt = (i, k, v) => setDraft({ ...d, alternatives: alts.map((x, j) => (j === i ? { ...x, [k]: v, origin: 'user' } : x)) });
    const findCases = () => run('cases', async () => { setFound(await api.findCases(job.id)); });
    const toggleCase = (id) => setDraft({ ...d, caseRef: { ...ref, ids: ref.ids.includes(id) ? ref.ids.filter(x => x !== id) : [...ref.ids, id].slice(0, 5) } });
    return (
      <div className="stn-step">
        {/* 튜닝 사례 참조 — 닮은 사례를 찾아 대조 자료로 싣는다(볼 수 있는 것만). 고르지 않으면 상위 몇 건이 자동으로 실린다 */}
        {aiEnabled && (
          <div className="stn-box stn-caseref">
            <div className="stn-row stn-between">
              <label className="stn-row"><input type="checkbox" checked={ref.enabled !== false} onChange={e => setDraft({ ...d, caseRef: { ...ref, enabled: e.target.checked } })} /> <b>{t('common:srv.caseRef')}</b></label>
              <button type="button" className="app-btn" onClick={findCases} disabled={running || ref.enabled === false}><FiSearch size={12} /> {t('common:srv.findCases')}</button>
            </div>
            <span className="stn-note">{t('common:srv.caseRefHint')}</span>
            {found && !(found.cases || []).length && <span className="stn-note">{t('common:srv.noCases')}</span>}
            {found && (found.cases || []).map(c => (
              <label key={c.id} className="stn-case">
                <input type="checkbox" checked={ref.ids.includes(c.id)} onChange={() => toggleCase(c.id)} />
                <span className="stn-case-body">
                  {c.match && <span className={`stn-match m-${c.match.tier}`}>{t(matchKey(c.match))}</span>}
                  <b>{c.title}</b>{c.perfBasis === 'estimated' ? <span className="stn-note"> · {t('common:stn.caseEstimated')}</span> : null}
                  <span className="stn-note">{c.symptom || c.improvement || ''}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        {aiEnabled && <AiBar label={t('common:srv.aiAlternatives')} has={alts.length > 0} onRun={ins => ai('alternatives', ins)} onStop={stopAi} running={busy === 'ai:alternatives'} disabled={running || !dIds.length} />}
        {!dIds.length && <div className="stn-note">{t('common:srv.needDiagnosis')}</div>}
        {alts.map((a, i) => (
          <div key={a.id || `new-${i}`} className="stn-card">
            <div className="stn-row stn-between">
              <span className="stn-row">
                <span className="stn-pl-badge static">{a.id || `#${i + 1}`}</span>
                {a.origin === 'ai' && <span className="stn-badge">AI</span>}
                <span className="srv-estimate">{t('common:srv.estimate')}</span>
                <input className="stn-grow" value={a.title || ''} placeholder={t('common:srv.altTitle')} onChange={e => setAlt(i, 'title', e.target.value)} />
              </span>
              <button type="button" className="app-btn app-btn-danger" title={t('common:ui.delete')} onClick={() => setDraft({ ...d, alternatives: alts.filter((_, j) => j !== i) })}><FiTrash2 size={11} /></button>
            </div>
            <div className="stn-row">
              <span className="stn-note">{t('common:srv.targets')}</span>
              {dIds.map(id => (
                <label key={id} className="stn-row srv-target"><input type="checkbox" checked={(a.targets || []).includes(id)}
                  onChange={e => setAlt(i, 'targets', e.target.checked ? [...(a.targets || []), id] : (a.targets || []).filter(x => x !== id))} /> {id}</label>
              ))}
            </div>
            <div className="stn-row stn-codeRow">
              <CodeChips label={t('common:stn.codes.fix')} axis="fix" codes={a.fixCodes} catalog={catalog} onChange={v => setAlt(i, 'fixCodes', v)} />
            </div>
            <label className="stn-field"><span>{t('common:srv.altSql')}</span><textarea className="srv-mono" rows={6} value={a.sql || ''} onChange={e => setAlt(i, 'sql', e.target.value)} /></label>
            <label className="stn-field"><span>{t('common:srv.altIndex')}</span><textarea className="srv-mono" rows={2} value={a.indexDdl || ''} onChange={e => setAlt(i, 'indexDdl', e.target.value)} /></label>
            <div className="stn-grid2">
              <label className="stn-field"><span>{t('common:srv.rationale')}</span><textarea rows={3} value={a.rationale || ''} onChange={e => setAlt(i, 'rationale', e.target.value)} /></label>
              <label className="stn-field"><span>{t('common:srv.expected')}</span><textarea rows={3} value={a.expected || ''} onChange={e => setAlt(i, 'expected', e.target.value)} /></label>
            </div>
            <label className="stn-field"><span>{t('common:srv.assumption')}</span><textarea rows={2} value={a.assumption || ''} onChange={e => setAlt(i, 'assumption', e.target.value)} /></label>
            {(a.caseIds || []).length > 0 && <div className="stn-note"><FiBookOpen size={11} /> {t('common:srv.refCases', { ids: a.caseIds.join(', ') })}</div>}
          </div>
        ))}
        <div className="stn-actions">
          <button type="button" className="app-btn" onClick={() => setDraft({ ...d, alternatives: [...alts, { title: '', sql: '', indexDdl: '', targets: [], rationale: '', expected: '', assumption: '', caseIds: [], origin: 'user' }] })}><FiPlus size={12} /> {t('common:srv.addAlt')}</button>
        </div>
      </div>
    );
  };

  const renderFinal = () => {
    const alts = d.alternatives || [];
    const f = d.final || { summary: '', ranking: [] };
    const setRanking = (ranking) => setDraft({ ...d, final: { ...f, ranking, origin: 'user' } });
    const move = (i, by) => { const r = [...f.ranking]; const j = i + by; if (j < 0 || j >= r.length) return; [r[i], r[j]] = [r[j], r[i]]; setRanking(r); };
    const rest = alts.filter(a => !f.ranking.some(r => r.alt === a.id));
    return (
      <div className="stn-step">
        {aiEnabled && <AiBar label={t('common:srv.aiFinal')} has={!!f.summary || f.ranking.length > 0} onRun={ins => ai('final', ins)} onStop={stopAi} running={busy === 'ai:final'} disabled={running || !alts.length} />}
        {!alts.length && <div className="stn-note">{t('common:srv.needAlternatives')}</div>}
        <label className="stn-field"><span>{t('common:srv.finalSummary')}</span>
          <textarea rows={3} value={f.summary || ''} onChange={e => setDraft({ ...d, final: { ...f, summary: e.target.value, origin: 'user' } })} />
        </label>
        {f.ranking.map((r, i) => {
          const a = alts.find(x => x.id === r.alt);
          return (
            <div key={r.alt} className="stn-card">
              <div className="stn-row stn-between">
                <span className="stn-row"><b>{i + 1}.</b> <span className="stn-pl-badge static">{r.alt}</span> {a ? a.title : ''} <span className="srv-estimate">{t('common:srv.estimate')}</span></span>
                <span className="stn-row">
                  <button type="button" className="app-btn" onClick={() => move(i, -1)} disabled={i === 0}><FiArrowUp size={11} /></button>
                  <button type="button" className="app-btn" onClick={() => move(i, 1)} disabled={i === f.ranking.length - 1}><FiArrowDown size={11} /></button>
                  <button type="button" className="app-btn app-btn-danger" title={t('common:ui.delete')} onClick={() => setRanking(f.ranking.filter((_, j) => j !== i))}><FiTrash2 size={11} /></button>
                </span>
              </div>
              <textarea rows={3} value={r.reason || ''} placeholder={t('common:srv.reasonPh')} onChange={e => setRanking(f.ranking.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)))} />
            </div>
          );
        })}
        <div className="stn-actions">
          {rest.map(a => <button key={a.id} type="button" className="app-btn" onClick={() => setRanking([...f.ranking, { alt: a.id, reason: '' }])}><FiPlus size={12} /> {a.id} {a.title}</button>)}
        </div>
      </div>
    );
  };

  const renderReport = () => (
    <div className="stn-step">
      <ReportView
        markdown={report}
        loadingText={t('common:stn.loading')}
        toolbar={(
          <div className="stn-row stn-between">
            <span className="stn-note">{t('common:srv.reportHint')}</span>
            <span className="stn-row">
              <span className="stn-row stn-report-mode" role="group" aria-label={t('common:stn.reportScope')}>
                <button type="button" className={`app-btn${reportView === 'full' ? ' on' : ''}`} aria-pressed={reportView === 'full'} onClick={() => setReportView('full')}>{t('common:stn.reportFull')}</button>
                <button type="button" className={`app-btn${reportView === 'brief' ? ' on' : ''}`} aria-pressed={reportView === 'brief'} onClick={() => setReportView('brief')}>{t('common:stn.reportBrief')}</button>
              </span>
              <button type="button" className="app-btn" onClick={() => loadReport()} disabled={running}><FiRefreshCw size={12} /> {t('common:stn.refresh')}</button>
              {onRegisterCase && <button type="button" className="app-btn" onClick={registerCase} disabled={running} title={t('common:srv.registerCaseHint')}><FiBookOpen size={12} /> {t('common:stn.registerCase')}</button>}
              <button type="button" className="app-btn app-btn-primary" onClick={downloadReport} disabled={!report}><FiDownload size={12} /> {t('common:stn.download')}</button>
            </span>
          </div>
        )}
      />
    </div>
  );

  const renderHistory = () => {
    const hist = [...(job.doc.history || [])].reverse();
    return (
      <div className="stn-step">
        <div className="stn-table-wrap">
          <table className="stn-table">
            <thead><tr><th>{t('common:srv.histAt')}</th><th>{t('common:srv.histWho')}</th><th>{t('common:srv.histStep')}</th><th>{t('common:srv.histWhat')}</th><th>{t('common:srv.histNote')}</th></tr></thead>
            <tbody>
              {hist.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key -- 이력은 순서가 곧 키다
                <tr key={i}>
                  <td className="mono">{storedTime(h.at, 'min')}</td>
                  <td>{t(`common:srv.actor.${h.actor}`, { defaultValue: h.actor })}</td>
                  <td>{h.step ? t(`common:srv.steps.${h.step}`, { defaultValue: h.step }) : '-'}</td>
                  <td>{t(`common:srv.action.${h.action}`, { defaultValue: h.action })}</td>
                  <td className="stn-small">{h.note}</td>
                </tr>
              ))}
              {!hist.length && <tr><td colSpan={5} className="stn-note">{t('common:stn.noHistory')}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const body = !job || !d ? null : { input: renderInput, diagnose: renderDiagnose, alternatives: renderAlternatives, final: renderFinal, report: renderReport, history: renderHistory }[step]();

  return (
    <AppModal windowed open={open} onClose={onClose} title={t('common:srv.windowTitle')} icon={<FiFileText size={14} />} className="app-modal-default stn-modal">
      <div className="stn-body">
        <aside className="stn-side">
          <button type="button" className="app-btn app-btn-primary stn-full" onClick={() => { setCreating({ title: '', sql: '', planText: '', info: '', sourceNote: '' }); adopt(null); }}><FiPlus size={12} /> {t('common:srv.newJob')}</button>
          <input className="stn-full" value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:stn.jobSearch')} onKeyDown={e => { if (e.key === 'Enter') reload(); }} />
          <ul className="stn-jobs">
            {jobs.map(j => (
              <li key={j.id} className={job && job.id === j.id ? 'on' : ''} onClick={() => openJob(j.id)}>
                <div className="stn-job-title">{j.title}</div>
                <div className="stn-job-meta">{t(`common:srv.steps.${j.status}`, { defaultValue: j.status })} · {storedTime(j.updatedAt, 'date')}</div>
              </li>
            ))}
            {!jobs.length && <li className="stn-empty">{t('common:stn.noJobs')}</li>}
          </ul>
        </aside>
        <section className="stn-main">
          {/* 이 기능의 성격 — 늘 보인다. 측정으로 검증하는 SQL 튜닝과 헷갈리지 않게 */}
          <div className="srv-banner" role="note"><FiAlertTriangle size={12} /> {t('common:srv.banner')}</div>
          {creating && (
            <div className="stn-new">
              <label className="stn-field"><span>{t('common:stn.title')}</span>
                <input value={creating.title} onChange={e => setCreating({ ...creating, title: e.target.value })} placeholder={t('common:stn.titlePh')} />
              </label>
              <InputFields value={creating} onChange={setCreating} aiEnabled={aiEnabled} provider={aiProvider} disabled={running} />
              <div className="stn-actions">
                <button type="button" className="app-btn" onClick={() => setCreating(null)}>{t('common:ui.cancel')}</button>
                <button type="button" className="app-btn app-btn-primary" onClick={create} disabled={running || !(creating.sql.trim() || creating.planText.trim())}><FiPlus size={12} /> {t('common:srv.create')}</button>
              </div>
            </div>
          )}
          {!creating && !job && <div className="stn-empty-main">{t('common:srv.intro')}</div>}
          {!creating && job && d && (
            <>
              <WorkflowHead title={job.title}
                badges={job.readOnly ? <span className="stn-warn">{t('common:stn.readOnly')}</span> : null}
                dirty={stepIsDirty}
                actions={job.doc.ai && job.doc.ai.calls > 0 ? <span className="stn-note">{t('common:stn.aiUsage', { calls: job.doc.ai.calls, tokens: fmt(job.doc.ai.tokens), cost: job.doc.ai.costUsd })}</span> : null}
                onSave={stepSave ? saveStep : null} saveDisabled={running || !stepIsDirty}
                onDelete={removeJob} deleteDisabled={running} />
              <WorkflowRibbon tabs={TABS} step={step} onStep={setStep} label={s => t(`common:srv.steps.${s}`)}
                suffix={s => (s === 'history' && (job.doc.history || []).length > 0 ? ` (${job.doc.history.length})` : '')} />
              {/* 지금 단계에서 할 일 */}
              {step !== 'history' && <div className="srv-stepHelp" key={`help-${step}`}>{t(`common:srv.stepHelp.${step}`)}</div>}
              {busy && busy.startsWith('ai:') && <div className="stn-progress">{t('common:srv.aiRunning')}</div>}
              {problems.length > 0 && <div className="stn-problems">{problems.map(p => <div key={p.code + (p.alt || '')}>{p.message}</div>)}</div>}
              <div className="stn-step-wrap" key={`${job.id}-${step}`}>{body}</div>
            </>
          )}
        </section>
      </div>
    </AppModal>
  );
};

SqlReviewModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** AI 빌드인가 — 아니면 AI 단추(자료 읽기·진단·개선안·최종안)와 사례 참조가 없다. 모든 칸은 손으로 쓸 수 있다 */
  aiEnabled: PropTypes.bool,
  aiProvider: PropTypes.string,
  /** 다른 화면에서 넘어온 자료 { sql, planText, info, sourceNote, title, nonce } — 새 작업 양식에 채운다 */
  seed: PropTypes.shape({ sql: PropTypes.string, planText: PropTypes.string, nonce: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  onSeedUsed: PropTypes.func,
  /** 튜닝 가이드 관리 창을 연다 */
  onOpenGuides: PropTypes.func,
  /** 새로고침 전에 보던 위치 { id, step } — 처음 열릴 때 한 번 되돌린다 */
  restore: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string }),
  /** 채팅 에이전트가 가리킨 작업(SSE open_screen, 2026-10-20) — nonce 가 바뀔 때마다 그 작업·단계를 연다 */
  focus: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string, nonce: PropTypes.number }),
  /** 보는 위치가 바뀔 때 ({ id, step }) — App 이 배치에 저장한다 */
  onView: PropTypes.func,
  /** 결과를 튜닝 사례 등록 화면으로(AI 빌드에서만) */
  onRegisterCase: PropTypes.func,
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(SqlReviewModal);
